import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";

import { api, ApiError } from "../api";
import { parseTaka, taka } from "../format";
import { BannersTab } from "../store/Banners";
import { BulkUpload } from "../store/BulkUpload";
import { prepareImage } from "../store/images";
import { imageSrc, type OpsProduct, type ProductPage, type StoreMeta } from "../store/types";

type Tab = "products" | "bulk" | "banners";

/** The Store: its products, bulk upload (CSV + ZIP of photos) and the banners on the app's Store tab. */
export default function OpsStore() {
  const [tab, setTab] = useState<Tab>("products");
  const meta = useQuery({ queryKey: ["store-meta"], queryFn: () => api<StoreMeta>("/ops/store/meta") });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-xl font-semibold">Store</h1>
        {(
          [
            ["products", "Products"],
            ["bulk", "Bulk upload"],
            ["banners", "Banners"],
          ] as const
        ).map(([id, label]) => (
          <Button key={id} size="sm" variant={tab === id ? "secondary" : "ghost"} onClick={() => setTab(id)}>
            {label}
          </Button>
        ))}
      </div>
      {tab === "products" && <ProductsTab meta={meta.data ?? null} />}
      {tab === "bulk" && <BulkUpload meta={meta.data ?? null} />}
      {tab === "banners" && <BannersTab meta={meta.data ?? null} />}
    </div>
  );
}

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.code.replace(/_/g, " ") : err instanceof Error ? err.message : fallback);

function ProductsTab({ meta }: { meta: StoreMeta | null }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [noPhoto, setNoPhoto] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<OpsProduct | "new" | null>(null);
  const q = search.trim();
  useEffect(() => setPage(1), [q, category, noPhoto]);
  const list = useQuery({
    queryKey: ["store-products", q, category, noPhoto, page],
    queryFn: () =>
      api<ProductPage>(
        `/ops/store/products?page=${page}${q ? `&q=${encodeURIComponent(q)}` : ""}${category ? `&category=${category}` : ""}${noPhoto ? "&noPhoto=1" : ""}`,
      ),
    placeholderData: keepPreviousData,
  });
  const catName = (key: string) => meta?.categories.find((c) => c.key === key)?.en ?? key;

  if (editing) return <ProductForm meta={meta} product={editing === "new" ? null : editing} onDone={() => setEditing(null)} />;
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">
          Products
          {list.data && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {list.data.counts.all} in all · {list.data.counts.all - list.data.counts.withPhoto} without a photo
            </span>
          )}
        </CardTitle>
        <Button size="sm" onClick={() => setEditing("new")}>
          Add product
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Input className="min-w-48 flex-1" placeholder="Search name, brand or SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="h-9 rounded-md border bg-background px-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All departments</option>
            {meta?.categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.en}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={noPhoto} onChange={(e) => setNoPhoto(e.target.checked)} />
            No photo
          </label>
        </div>
        {list.isError && <p className="text-sm text-destructive">{errorText(list.error, "Could not load products.")}</p>}
        <div className="divide-y">
          {list.data?.products.map((p) => (
            <button key={p.id} type="button" onClick={() => setEditing(p)} className="flex w-full items-center gap-3 py-2 text-left text-sm hover:bg-muted/50">
              <Thumb src={imageSrc(p.imageUrl)} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{p.name}</div>
                <div className="truncate text-muted-foreground">
                  {[p.brand, p.packLabel, catName(p.category), p.subcategory].filter(Boolean).join(" · ")} · {p.sku}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                {!p.active && <Badge variant="outline">Hidden</Badge>}
                {!p.inStock && <Badge variant="outline">Out of stock</Badge>}
                {p.rxRequired && <Badge variant="outline">Rx</Badge>}
                {p.featured && <Badge>Best pick</Badge>}
                <span className="w-24 text-right font-medium">
                  {taka(p.pricePaisa)}
                  {p.mrpPaisa && p.mrpPaisa > p.pricePaisa ? <span className="ml-1 text-xs text-muted-foreground line-through">{taka(p.mrpPaisa)}</span> : null}
                </span>
              </div>
            </button>
          ))}
          {list.data && list.data.products.length === 0 && <p className="py-4 text-sm text-muted-foreground">No products. Add one, or use Bulk upload.</p>}
        </div>
        {list.data && list.data.pages > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span>
              Page {list.data.page} of {list.data.pages} ({list.data.total})
            </span>
            <Button size="sm" variant="outline" disabled={page >= list.data.pages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Thumb({ src, size = 48 }: { src: string | null; size?: number }) {
  return (
    <div className="shrink-0 overflow-hidden rounded-md border bg-white" style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" loading="lazy" className="h-full w-full object-contain" /> : <div className="flex h-full items-center justify-center text-[10px] text-muted-foreground">No photo</div>}
    </div>
  );
}

type Form = {
  sku: string;
  name: string;
  nameBn: string;
  brand: string;
  category: string;
  subcategory: string;
  packLabel: string;
  groupKey: string;
  price: string;
  mrp: string;
  inStock: boolean;
  active: boolean;
  featured: boolean;
  rxRequired: boolean;
  sort: string;
  description: string;
  descriptionBn: string;
};

const toForm = (p: OpsProduct | null): Form => ({
  sku: p?.sku ?? "",
  name: p?.name ?? "",
  nameBn: p?.nameBn ?? "",
  brand: p?.brand ?? "",
  category: p?.category ?? "",
  subcategory: p?.subcategory ?? "",
  packLabel: p?.packLabel ?? "",
  groupKey: p?.groupKey ?? "",
  price: p ? String(p.pricePaisa / 100) : "",
  mrp: p?.mrpPaisa ? String(p.mrpPaisa / 100) : "",
  inStock: p?.inStock ?? true,
  active: p?.active ?? true,
  featured: p?.featured ?? false,
  rxRequired: p?.rxRequired ?? false,
  sort: String(p?.sort ?? 0),
  description: p?.description ?? "",
  descriptionBn: p?.descriptionBn ?? "",
});

function ProductForm({ meta, product, onDone }: { meta: StoreMeta | null; product: OpsProduct | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(() => toForm(product));
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(imageSrc(product?.imageUrl ?? null));
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const price = parseTaka(f.price);
      const mrp = f.mrp.trim() ? parseTaka(f.mrp) : null;
      if (!f.name.trim()) throw new Error("Give the product a name.");
      if (!f.category) throw new Error("Choose a department.");
      if (price === null) throw new Error("The price is not a price.");
      if (f.mrp.trim() && mrp === null) throw new Error("The MRP is not a price.");
      const body = {
        ...(f.sku.trim() ? { sku: f.sku.trim() } : {}),
        name: f.name,
        nameBn: f.nameBn,
        brand: f.brand,
        category: f.category,
        subcategory: f.subcategory,
        packLabel: f.packLabel,
        groupKey: f.groupKey.trim() || null,
        pricePaisa: price,
        mrpPaisa: mrp,
        inStock: f.inStock,
        active: f.active,
        featured: f.featured,
        rxRequired: f.rxRequired,
        sort: Number(f.sort) || 0,
        description: f.description,
        descriptionBn: f.descriptionBn,
      };
      const saved = product
        ? await api<{ product: OpsProduct }>(`/ops/store/products/${product.id}`, { method: "PATCH", body })
        : await api<{ product: OpsProduct }>("/ops/store/products", { body });
      if (photo) {
        const img = await prepareImage(photo);
        await api(`/ops/store/products/${saved.product.id}/image`, { method: "PUT", body: { data: img.data } });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store-products"] });
      onDone();
    },
    onError: (err) => setError(errorText(err, "Could not save.")),
  });

  const remove = useMutation({
    mutationFn: () => api(`/ops/store/products/${product!.id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["store-products"] });
      onDone();
    },
    onError: (err) => setError(err instanceof ApiError && err.code === "in_orders" ? "It has been ordered, so it cannot be deleted. Untick Shown in the app instead." : errorText(err, "Could not delete.")),
  });

  const field = (label: string, input: React.ReactNode, hint?: string) => (
    <label className="block space-y-1 text-sm">
      <span className="font-medium">{label}</span>
      {input}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
  const text = (k: keyof Form, placeholder = "") => <Input value={f[k] as string} placeholder={placeholder} onChange={(e) => set(k, e.target.value as never)} />;
  const check = (k: "inStock" | "active" | "featured" | "rxRequired", label: string) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" className="h-4 w-4" checked={f[k]} onChange={(e) => set(k, e.target.checked)} />
      {label}
    </label>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{product ? `Edit: ${product.name}` : "Add a product"}</CardTitle>
        <Button size="sm" variant="outline" onClick={onDone}>
          Back
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-start gap-4">
          <div className="space-y-2">
            <div className="h-40 w-40 overflow-hidden rounded-lg border bg-white">
              {preview ? <img src={preview} alt="" className="h-full w-full object-contain" /> : <div className="flex h-full items-center justify-center text-xs text-muted-foreground">No photo</div>}
            </div>
            <input
              type="file"
              accept="image/*"
              className="w-40 text-xs"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setPhoto(file);
                if (file) setPreview(URL.createObjectURL(file));
              }}
            />
          </div>
          <div className="grid min-w-64 flex-1 gap-3 sm:grid-cols-2">
            {field("Name", text("name", "Quaker Oats 450g Jar"))}
            {field("Name in Bangla", text("nameBn"))}
            {field("Brand", text("brand"))}
            {field("Pack", text("packLabel", "450 gm jar"), "What one item is.")}
            {field(
              "Department",
              <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={f.category} onChange={(e) => set("category", e.target.value)}>
                <option value="">Choose…</option>
                {meta?.categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.en}
                  </option>
                ))}
              </select>,
            )}
            {field("Shelf", text("subcategory", "Cleansers"), "A filter within the department.")}
            {field("Price (৳)", <Input inputMode="decimal" value={f.price} onChange={(e) => set("price", e.target.value)} />, "What the customer pays for one.")}
            {field("MRP (৳)", <Input inputMode="decimal" value={f.mrp} onChange={(e) => set("mrp", e.target.value)} />, "Shown struck through when higher.")}
            {field("SKU", text("sku"), product ? undefined : "Left empty, one is made.")}
            {field("Size group", text("groupKey", "quaker-oats"), "Products with the same group show as sizes of one product.")}
            {field("Order", <Input inputMode="numeric" value={f.sort} onChange={(e) => set("sort", e.target.value)} />, "Lower comes first.")}
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          {check("active", "Shown in the app")}
          {check("inStock", "In stock")}
          {check("featured", "Best pick")}
          {check("rxRequired", "Prescription needed")}
        </div>
        {field("Description", <textarea className="min-h-24 w-full rounded-md border bg-background p-2 text-sm" value={f.description} onChange={(e) => set("description", e.target.value)} />)}
        {field("Description in Bangla", <textarea className="min-h-20 w-full rounded-md border bg-background p-2 text-sm" value={f.descriptionBn} onChange={(e) => set("descriptionBn", e.target.value)} />)}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {product && (
            <Button
              variant="outline"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete ${product.name}?`)) remove.mutate();
              }}
            >
              Delete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
